/**
 * Country flag resolution.
 *
 * Team/country names in event data are free-form strings authored by admins
 * (e.g. "Argentina", "USA", "South Korea"). This maps those names to one of the
 * optimized flag assets in `public/assets/countries/` and returns null when no
 * flag exists — so non-country sports (NBA, NCAA, etc.) render no flag at all.
 *
 * Matching is forgiving: diacritics, casing, punctuation, and common naming
 * variants ("Ivory Coast" ↔ "Côte d'Ivoire", "DR Congo" ↔ "Congo DR") all
 * resolve to the same asset.
 */
import { asset } from './asset';

/** Asset slugs that exist in public/assets/countries (filenames without .png). */
const FLAG_SLUGS = new Set<string>([
  'algeria', 'argentina', 'australia', 'austria', 'belgium', 'bosnia', 'brazil',
  'canada', 'capeverde', 'colombia', 'congodr', 'cotedivoire', 'croatia',
  'curacao', 'czechia', 'ecuador', 'egypt', 'england', 'france', 'germany',
  'ghana', 'haiti', 'iran', 'iraq', 'japan', 'jordan', 'mexico', 'morocco',
  'netherlands', 'newzealand', 'norway', 'panama', 'paraguay', 'portugal',
  'qatar', 'republicofkorea', 'saudiarabia', 'scotland', 'senegal',
  'southafrica', 'spain', 'sweden', 'switzerland', 'tunisia', 'turkey',
  'uruguay', 'usa', 'uzbekistan',
]);

/**
 * Aliases mapping a normalized name (lowercase, no diacritics, alphanumeric
 * only) to a flag slug. Used when the data name doesn't match the slug exactly.
 */
const ALIASES: Record<string, string> = {
  // USA
  unitedstates: 'usa',
  unitedstatesofamerica: 'usa',
  us: 'usa',
  usmnt: 'usa',
  uswnt: 'usa',
  america: 'usa',
  // South Korea
  southkorea: 'republicofkorea',
  korea: 'republicofkorea',
  korearepublic: 'republicofkorea',
  skorea: 'republicofkorea',
  // Côte d'Ivoire
  ivorycoast: 'cotedivoire',
  // DR Congo
  drcongo: 'congodr',
  congo: 'congodr',
  democraticrepublicofthecongo: 'congodr',
  democraticrepublicofcongo: 'congodr',
  congokinshasa: 'congodr',
  // Czechia
  czechrepublic: 'czechia',
  czech: 'czechia',
  // Bosnia
  bosniaandherzegovina: 'bosnia',
  bosniaherzegovina: 'bosnia',
  // Cape Verde
  caboverde: 'capeverde',
  // Türkiye
  turkiye: 'turkey',
  // Great Britain / UK fall back to England flag
  unitedkingdom: 'england',
  greatbritain: 'england',
  uk: 'england',
  gb: 'england',
};

/** Normalize a name: strip diacritics, lowercase, keep only a-z0-9. */
function normalize(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/** Resolve a team/country name to a flag slug, or null if there's no match. */
export function countryFlagSlug(name: string | null | undefined): string | null {
  if (!name) return null;
  // Strip a leading seed prefix like "(3) Argentina".
  const clean = name.replace(/^\(\d+\)\s*/, '');
  const key = normalize(clean);
  if (!key) return null;
  if (FLAG_SLUGS.has(key)) return key;
  if (ALIASES[key]) return ALIASES[key];
  return null;
}

/** Resolve a team/country name to a flag asset URL, or null if there's no match. */
export function countryFlagSrc(name: string | null | undefined): string | null {
  const slug = countryFlagSlug(name);
  return slug ? asset(`assets/countries/${slug}.webp`) : null;
}

// ============================================================================
// Country codes (FIFA 3-letter) and emoji flags.
// ============================================================================

/**
 * Per-slug FIFA 3-letter code (the broadcast standard: CZE, KOR, RSA, GER…)
 * and ISO 3166 code used to derive the emoji flag. England/Scotland use
 * subdivision codes, which become emoji tag-sequence flags.
 */
const COUNTRY_CODES: Record<string, { code: string; iso: string }> = {
  algeria: { code: 'ALG', iso: 'DZ' },
  argentina: { code: 'ARG', iso: 'AR' },
  australia: { code: 'AUS', iso: 'AU' },
  austria: { code: 'AUT', iso: 'AT' },
  belgium: { code: 'BEL', iso: 'BE' },
  bosnia: { code: 'BIH', iso: 'BA' },
  brazil: { code: 'BRA', iso: 'BR' },
  canada: { code: 'CAN', iso: 'CA' },
  capeverde: { code: 'CPV', iso: 'CV' },
  colombia: { code: 'COL', iso: 'CO' },
  congodr: { code: 'COD', iso: 'CD' },
  cotedivoire: { code: 'CIV', iso: 'CI' },
  croatia: { code: 'CRO', iso: 'HR' },
  curacao: { code: 'CUW', iso: 'CW' },
  czechia: { code: 'CZE', iso: 'CZ' },
  ecuador: { code: 'ECU', iso: 'EC' },
  egypt: { code: 'EGY', iso: 'EG' },
  england: { code: 'ENG', iso: 'GB-ENG' },
  france: { code: 'FRA', iso: 'FR' },
  germany: { code: 'GER', iso: 'DE' },
  ghana: { code: 'GHA', iso: 'GH' },
  haiti: { code: 'HAI', iso: 'HT' },
  iran: { code: 'IRN', iso: 'IR' },
  iraq: { code: 'IRQ', iso: 'IQ' },
  japan: { code: 'JPN', iso: 'JP' },
  jordan: { code: 'JOR', iso: 'JO' },
  mexico: { code: 'MEX', iso: 'MX' },
  morocco: { code: 'MAR', iso: 'MA' },
  netherlands: { code: 'NED', iso: 'NL' },
  newzealand: { code: 'NZL', iso: 'NZ' },
  norway: { code: 'NOR', iso: 'NO' },
  panama: { code: 'PAN', iso: 'PA' },
  paraguay: { code: 'PAR', iso: 'PY' },
  portugal: { code: 'POR', iso: 'PT' },
  qatar: { code: 'QAT', iso: 'QA' },
  republicofkorea: { code: 'KOR', iso: 'KR' },
  saudiarabia: { code: 'KSA', iso: 'SA' },
  scotland: { code: 'SCO', iso: 'GB-SCT' },
  senegal: { code: 'SEN', iso: 'SN' },
  southafrica: { code: 'RSA', iso: 'ZA' },
  spain: { code: 'ESP', iso: 'ES' },
  sweden: { code: 'SWE', iso: 'SE' },
  switzerland: { code: 'SUI', iso: 'CH' },
  tunisia: { code: 'TUN', iso: 'TN' },
  turkey: { code: 'TUR', iso: 'TR' },
  uruguay: { code: 'URU', iso: 'UY' },
  usa: { code: 'USA', iso: 'US' },
  uzbekistan: { code: 'UZB', iso: 'UZ' },
};

/**
 * Build an emoji flag from an ISO 3166 code. Plain alpha-2 codes ("CZ") map to
 * regional-indicator pairs; subdivision codes ("GB-ENG") map to tag-sequence
 * flags (🏴 + tags), which is how England/Scotland flags are encoded.
 */
function emojiFlag(iso: string): string {
  if (iso.includes('-')) {
    const tags = [...iso.replace('-', '').toLowerCase()]
      .map((c) => String.fromCodePoint(0xe0000 + c.charCodeAt(0)))
      .join('');
    return `\u{1F3F4}${tags}\u{E007F}`;
  }
  return [...iso.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + (c.charCodeAt(0) - 65)))
    .join('');
}

/**
 * Resolve a team/country name to its standard FIFA 3-letter code
 * (e.g. "Czechia" → "CZE", "South Korea" → "KOR", "South Africa" → "RSA"),
 * or null when the name isn't a known country.
 */
export function countryCode(name: string | null | undefined): string | null {
  const slug = countryFlagSlug(name);
  return (slug && COUNTRY_CODES[slug]?.code) || null;
}

const COUNTRY_DISPLAY_NAMES: Record<string, string> = {
  bosnia: 'Bosnia',
};

/** Resolve a compact display name for long country names in tight UI controls. */
export function countryDisplayName(name: string | null | undefined): string {
  if (!name) return '';
  const clean = name.replace(/^\(\d+\)\s*/, '').trim();
  const slug = countryFlagSlug(clean);
  return (slug && COUNTRY_DISPLAY_NAMES[slug]) || clean;
}

/** Resolve a team/country name to its emoji flag (e.g. "🇨🇿"), or null. */
export function countryFlagEmoji(name: string | null | undefined): string | null {
  const slug = countryFlagSlug(name);
  const entry = slug ? COUNTRY_CODES[slug] : undefined;
  return entry ? emojiFlag(entry.iso) : null;
}

// ============================================================================
// Country info (mock — placeholder data for the team info popover).
// Confederations are accurate; FIFA ranks are approximate stand-ins until we
// wire up a real data source.
// ============================================================================

export type Confederation = 'UEFA' | 'CONMEBOL' | 'CONCACAF' | 'CAF' | 'AFC' | 'OFC';

interface CountryInfoEntry {
  confederation: Confederation;
  fifaRank: number;
}

const COUNTRY_INFO: Record<string, CountryInfoEntry> = {
  // CONMEBOL
  argentina: { confederation: 'CONMEBOL', fifaRank: 1 },
  brazil: { confederation: 'CONMEBOL', fifaRank: 5 },
  uruguay: { confederation: 'CONMEBOL', fifaRank: 11 },
  colombia: { confederation: 'CONMEBOL', fifaRank: 13 },
  ecuador: { confederation: 'CONMEBOL', fifaRank: 24 },
  paraguay: { confederation: 'CONMEBOL', fifaRank: 50 },
  // UEFA
  france: { confederation: 'UEFA', fifaRank: 2 },
  spain: { confederation: 'UEFA', fifaRank: 3 },
  england: { confederation: 'UEFA', fifaRank: 4 },
  portugal: { confederation: 'UEFA', fifaRank: 6 },
  netherlands: { confederation: 'UEFA', fifaRank: 7 },
  belgium: { confederation: 'UEFA', fifaRank: 8 },
  croatia: { confederation: 'UEFA', fifaRank: 9 },
  germany: { confederation: 'UEFA', fifaRank: 10 },
  switzerland: { confederation: 'UEFA', fifaRank: 19 },
  austria: { confederation: 'UEFA', fifaRank: 22 },
  sweden: { confederation: 'UEFA', fifaRank: 25 },
  turkey: { confederation: 'UEFA', fifaRank: 26 },
  norway: { confederation: 'UEFA', fifaRank: 35 },
  czechia: { confederation: 'UEFA', fifaRank: 37 },
  scotland: { confederation: 'UEFA', fifaRank: 33 },
  bosnia: { confederation: 'UEFA', fifaRank: 70 },
  // CONCACAF
  usa: { confederation: 'CONCACAF', fifaRank: 16 },
  mexico: { confederation: 'CONCACAF', fifaRank: 15 },
  canada: { confederation: 'CONCACAF', fifaRank: 30 },
  panama: { confederation: 'CONCACAF', fifaRank: 34 },
  haiti: { confederation: 'CONCACAF', fifaRank: 95 },
  curacao: { confederation: 'CONCACAF', fifaRank: 90 },
  // CAF
  morocco: { confederation: 'CAF', fifaRank: 12 },
  senegal: { confederation: 'CAF', fifaRank: 18 },
  egypt: { confederation: 'CAF', fifaRank: 32 },
  algeria: { confederation: 'CAF', fifaRank: 38 },
  cotedivoire: { confederation: 'CAF', fifaRank: 40 },
  tunisia: { confederation: 'CAF', fifaRank: 41 },
  congodr: { confederation: 'CAF', fifaRank: 60 },
  southafrica: { confederation: 'CAF', fifaRank: 58 },
  capeverde: { confederation: 'CAF', fifaRank: 70 },
  ghana: { confederation: 'CAF', fifaRank: 73 },
  // AFC
  japan: { confederation: 'AFC', fifaRank: 17 },
  iran: { confederation: 'AFC', fifaRank: 20 },
  republicofkorea: { confederation: 'AFC', fifaRank: 23 },
  australia: { confederation: 'AFC', fifaRank: 24 },
  qatar: { confederation: 'AFC', fifaRank: 36 },
  saudiarabia: { confederation: 'AFC', fifaRank: 56 },
  uzbekistan: { confederation: 'AFC', fifaRank: 57 },
  iraq: { confederation: 'AFC', fifaRank: 58 },
  jordan: { confederation: 'AFC', fifaRank: 64 },
  // OFC
  newzealand: { confederation: 'OFC', fifaRank: 89 },
};

export interface CountryInfo {
  /** Original display name as supplied (e.g. "Korea Republic"). */
  name: string;
  flagSrc: string;
  confederation: Confederation;
  fifaRank: number;
}

/** Resolve mock info for a country name, or null if it isn't a known country. */
export function countryInfo(name: string | null | undefined): CountryInfo | null {
  const slug = countryFlagSlug(name);
  if (!slug) return null;
  const info = COUNTRY_INFO[slug];
  if (!info) return null;
  return {
    name: (name ?? '').replace(/^\(\d+\)\s*/, '').trim(),
    flagSrc: asset(`assets/countries/${slug}.webp`),
    confederation: info.confederation,
    fifaRank: info.fifaRank,
  };
}
